import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  FileText,
  Search,
  MoreHorizontal,
  Bookmark,
  Users,
  LayoutGrid,
  Briefcase,
  BarChart2,
  Settings,
  Shield,
  X
} from 'lucide-react';
import { useT } from '../../lib/i18n';
import { useCurrentUser } from '../../hooks/use-current-user';

type AccessLevel = 'all' | 'team' | 'sales' | 'admin' | 'market';

interface TabItem {
  readonly path: string;
  readonly labelKey: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly access: AccessLevel;
}

const primaryTabs: readonly TabItem[] = [
  { path: '/dashboard', labelKey: 'nav.home', icon: LayoutDashboard, access: 'all' },
  { path: '/profile', labelKey: 'nav.profile', icon: User, access: 'all' },
  { path: '/assignments', labelKey: 'nav.assignments', icon: FileText, access: 'all' },
  { path: '/matches', labelKey: 'nav.matches', icon: Search, access: 'all' }
] as const;

const moreTabs: readonly TabItem[] = [
  { path: '/my-assignments', labelKey: 'nav.myAssignments', icon: Bookmark, access: 'all' },
  { path: '/team', labelKey: 'nav.team', icon: Users, access: 'team' },
  { path: '/matrix', labelKey: 'nav.matrix', icon: LayoutGrid, access: 'team' },
  { path: '/sales', labelKey: 'nav.sales', icon: Briefcase, access: 'sales' },
  { path: '/market-insights', labelKey: 'nav.marketInsights', icon: BarChart2, access: 'market' },
  { path: '/settings', labelKey: 'nav.settings', icon: Settings, access: 'all' },
  { path: '/admin', labelKey: 'nav.admin', icon: Shield, access: 'admin' }
] as const;

export function BottomNav() {
  const tFn = useT();
  const [moreOpen, setMoreOpen] = useState(false);
  const { canAccessAdmin, canAccessSales, canAccessTeam, canAccessMarketInsights } =
    useCurrentUser();

  const visibleMoreTabs = moreTabs.filter((tab) => {
    switch (tab.access) {
      case 'admin':
        return canAccessAdmin;
      case 'sales':
        return canAccessSales;
      case 'team':
        return canAccessTeam;
      case 'market':
        return canAccessMarketInsights;
      default:
        return true;
    }
  });

  return (
    <>
      {/* More menu overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="absolute bottom-16 left-0 right-0 rounded-t-xl border-t border-border bg-card p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-xs font-semibold text-muted-foreground uppercase">More</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="rounded-md p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {visibleMoreTabs.map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 rounded-lg px-2 py-3 text-[10px] font-medium transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/60 hover:bg-secondary hover:text-foreground'
                    }`
                  }
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="text-center leading-tight">{tFn(tab.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card md:hidden">
        <div className="flex items-center justify-around">
          {primaryTabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.path === '/dashboard'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors min-h-[44px] justify-center ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`
              }
            >
              <tab.icon className="h-5 w-5" />
              <span>{tFn(tab.labelKey)}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className={`flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors min-h-[44px] justify-center ${
              moreOpen ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>{tFn('nav.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
}
